import { baseApi } from "../baseApi";
import { PaymentMethod, PaymentLink } from "@/types";

const chargesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createPaymentLink: builder.mutation<
      PaymentLink,
      {
        move_id: number;
        payment_link: {
          purpose: string;
          memo?: string;
          amount: number;
          processing_fee_percentage?: number;
          processing_fee_amount?: number;
          emails?: string[];
          phone?: string | null;
        };
        send?: boolean;
      }
    >({
      query: (data) => ({
        url: "payments/links",
        method: "POST",
        body: data,
      }),
    }),
    sendPaymentLink: builder.mutation<
      void,
      {
        payment_link_id: number;
        send_via?: "email" | "text";
      }
    >({
      query: (data) => ({
        url: `payments/links/${data.payment_link_id}/send`,
        method: "POST",
        body: { send_via: data.send_via },
      }),
    }),

    initializeCharge: builder.mutation<
      { id: string; client_secret: string },
      { amount: number; mover_id: number }
    >({
      query: (data) => ({
        url: "payments/charges/init",
        method: "POST",
        body: { amount: data.amount, mover_id: data.mover_id },
      }),
    }),
    createPaymentMethod: builder.mutation<
      PaymentMethod,
      {
        move_id: number;
        token: string;
        payment_intent_id: string;
      }
    >({
      query: (data) => ({
        url: "payments/charges/payment-method",
        method: "POST",
        body: {
          move_id: data.move_id,
          token: data.token,
          payment_intent_id: data.payment_intent_id,
        },
      }),
    }),
    createCharge: builder.mutation<
      void,
      {
        payment_intent_id?: string;
        move_id: number;
        amount: number;
        purpose: string;
        memo: string;
        payment_method_id?: number;
        payment_method_token?: string;
        processing_fee_percentage?: number;
        processing_fee_amount?: number;
        bill_to?: {
          name: string;
          address: string;
          zip: string;
          country: string;
        };
      }
    >({
      query: (data) => ({
        url: "payments/charges/capture",
        method: "POST",
        body: data,
      }),
    }),
    chargeActions: builder.mutation<
      void,
      {
        charge_id: string;
        action: "mark_as_paid" | "void";
      }
    >({
      query: (data) => ({
        url: `payments/charges/${data.charge_id}/actions`,
        method: "POST",
        body: { op: data.action },
      }),
    }),
    refundCharge: builder.mutation<void, { id: number; amount: number }>({
      query: (data) => ({
        url: `payments/charges/${data.id}/refund`,
        method: "POST",
        body: { amount: data.amount },
      }),
    }),
    processFinalPayment: builder.mutation<
      void,
      {
        moveId: number;
        amount: number;
        paymentMethod: { id: number } | { kind: string };
        processingFeeAmount: number;
      }
    >({
      query: (data) => ({
        url: `payments/charges/final-payment`,
        method: "POST",
        body: {
          move_id: data.moveId,
          amount: data.amount,
          processing_fee_amount: data.processingFeeAmount,
          payment_method: data.paymentMethod,
        },
      }),
    }),
    recordOfflinePayment: builder.mutation<
      void,
      {
        moveId: string;
        amount: number;
        payment_method: string;
        purpose: string;
        send_receipt: boolean;
      }
    >({
      query: (data) => ({
        url: `payments/charges/record-offline-payment`,
        method: "POST",
        body: {
          move_id: data.moveId,
          amount: data.amount,
          payment_method: data.payment_method,
          purpose: data.purpose,
          send_receipt: data.send_receipt,
        },
      }),
    }),
  }),
});

export const {
  useCreatePaymentLinkMutation,
  useCreatePaymentMethodMutation,
  useInitializeChargeMutation,
  useCreateChargeMutation,
  useChargeActionsMutation,
  useRefundChargeMutation,
  useProcessFinalPaymentMutation,
  useSendPaymentLinkMutation,
  useRecordOfflinePaymentMutation,
} = chargesApi;

export default chargesApi;
